/**
 * Projects Page
 * 專案基本資料管理頁面
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DateInput from '../components/DateInput';
import { logView, logCreate, logUpdate, logDelete } from '../utils/userLogHelper';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import projectsService from '../services/projectsService';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ActivityParticipant
} from '../types/projects';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const ProjectsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { user } = useAuth();
  const { reloadProjects } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [data, setData] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ========== 系統代碼選項 ==========
  const [parties, setParties] = useState<SystemCode[]>([]);
  const [methodologies, setMethodologies] = useState<SystemCode[]>([]);
  const [sectoralScopes, setSectoralScopes] = useState<SystemCode[]>([]);
  const [projectTypes, setProjectTypes] = useState<SystemCode[]>([]);
  const [pddStatuses, setPddStatuses] = useState<SystemCode[]>([]);

  // ========== 表單資料 ==========
  const [formData, setFormData] = useState<ProjectCreate>({
    organization_id: user?.organization_id || 0,
    project_title: '',
    pdd_no: '',
    host_party: 0,
    participating_party: 0,
    activity_participants: [],
    pdd_version: '',
    pdd_completedate: undefined,
    methodologies: [],
    standard_baselines: '',
    sectoral_scope: [],
    project_type: 0,
    estimated_annual_reducevalue: 0,
    pdd_status: 0,
    is_active: true
  });

  // ========== 活動參與者狀態 ==========
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<{participant: ActivityParticipant, index: number} | null>(null);
  const [participantFormData, setParticipantFormData] = useState<ActivityParticipant>({
    ap_type: 1,
    ap_name: '',
    auth_party: 0
  });

  // ========== 分頁 ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== 多語系顯示輔助函數 ==========
  // 規範 6.5: HTML 安全渲染（僅允許 sub 和 sup 標籤）
  const renderHTML = (html: string) => {
    if (!html) return '';
    const sanitized = html
      .replace(/<(?!\/?su[bp]>)/g, '&lt;')
      .replace(/(?<!<\/?su[bp])>/g, '&gt;');
    return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  // 輔助函數: 將 HTML 轉換為純文字顯示（用於下拉選單）
  // 例如: "CO<sub>2</sub>" → "CO₂"
  const htmlToText = (html: string) => {
    if (!html) return '';
    // 替換 <sub> 和 <sup> 為 Unicode 字元
    let text = html
      .replace(/<sub>(.*?)<\/sub>/g, (_, content) => {
        // 數字下標對應表
        const subscripts: { [key: string]: string } = {
          '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
          '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉'
        };
        return content.split('').map((c: string) => subscripts[c] || c).join('');
      })
      .replace(/<sup>(.*?)<\/sup>/g, (_, content) => {
        // 數字上標對應表
        const superscripts: { [key: string]: string } = {
          '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
          '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
          '+': '⁺', '-': '⁻'
        };
        return content.split('').map((c: string) => superscripts[c] || c).join('');
      });
    // 移除其他 HTML 標籤
    text = text.replace(/<[^>]*>/g, '');
    return text;
  };

  // 規範 1-2: 渲染系統代碼名稱（表格中：中文只顯示中文，英文只顯示英文）
  const renderSystemCodeName = (code: SystemCode | undefined) => {
    if (!code) return '-';
    const isChinese = i18n.language === 'zh-TW';

    // 表格中：中文語系只顯示中文，英文語系只顯示英文
    if (isChinese) {
      return renderHTML(code.code_cname);
    } else {
      return renderHTML(code.code_ename || code.code_cname);
    }
  };

  // 渲染系統代碼名稱陣列（多個項目）
  const renderSystemCodeNames = (codes: any[] | undefined) => {
    if (!codes || codes.length === 0) return '-';
    const isChinese = i18n.language === 'zh-TW';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {codes.map((code, index) => (
          <div key={index} style={{ fontSize: '0.95em' }}>
            {/* 表格中：中文語系只顯示中文，英文語系只顯示英文 */}
            {isChinese ? renderHTML(code.code_cname) : renderHTML(code.code_ename || code.code_cname)}
          </div>
        ))}
      </div>
    );
  };

  // ========== 初始化: 驗證 Session + 載入資料 ==========
  useEffect(() => {
    if (!permissionLoading && hasPermission('projects', 'read') && !hasInitialized.current) {
      hasInitialized.current = true;
      const initPage = async () => {
        try {
          const isValid = await validateSession('ProjectsPage');
          if (!isValid) {
            return;
          }

          await loadSystemCodes();
          await loadData();
          await logView('projects', {});
        } catch (err: any) {
          const errorMsg = err.response?.data?.detail || err.message || t('message.loadFailed');
          setError(errorMsg);
          await logView('projects', {}, errorMsg);
        }
      };
      initPage();
    }
  }, [permissionLoading]);

  // ========== 載入系統代碼選項 ==========
  const loadSystemCodes = async () => {
    try {
      const codes = await getSystemCodes();
      setParties(codes.filter((c: SystemCode) => c.code_etype === 'Article 6.4 Party'));

      // 方法論篩選：包含三種類型
      setMethodologies(codes.filter((c: SystemCode) =>
        c.code_etype === 'Methodologies of ACM' ||
        c.code_etype === 'Methodologies of AM' ||
        c.code_etype === 'Methodological Tools'
      ));

      setSectoralScopes(codes.filter((c: SystemCode) => c.code_etype === 'Sectoral Scopes'));
      setProjectTypes(codes.filter((c: SystemCode) => c.code_etype === 'project_type'));
      setPddStatuses(codes.filter((c: SystemCode) => c.code_etype === 'pdd_status'));
    } catch (error: any) {
      console.error('Failed to load system codes:', error);
    }
  };

  // ========== 載入專案資料 ==========
  const loadData = async () => {
    setLoading(true);
    try {
      const result = await projectsService.getAll({});
      setData(result);
      setError(null);
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('message.loadFailed');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ========== 新增對話框 ==========
  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      organization_id: user?.organization_id || 0,
      project_title: '',
      pdd_no: '',
      host_party: parties[0]?.id || 0,
      participating_party: parties[0]?.id || 0,
      activity_participants: [],
      pdd_version: '',
      pdd_completedate: undefined,
      methodologies: [],
      standard_baselines: '',
      sectoral_scope: [],
      project_type: projectTypes[0]?.id || 0,
      estimated_annual_reducevalue: 0,
      pdd_status: pddStatuses[0]?.id || 0,
      is_active: true
    });
    setShowModal(true);
  };

  // ========== 編輯對話框 ==========
  const handleEdit = (item: Project) => {
    setEditingItem(item);
    setFormData({
      organization_id: item.organization_id,
      project_title: item.project_title,
      pdd_no: item.pdd_no || '',
      host_party: item.host_party,
      participating_party: item.participating_party,
      activity_participants: item.activity_participants || [],
      pdd_version: item.pdd_version || '',
      pdd_completedate: item.pdd_completedate,
      methodologies: item.methodologies,
      standard_baselines: item.standard_baselines || '',
      sectoral_scope: item.sectoral_scope || [],
      project_type: item.project_type,
      estimated_annual_reducevalue: item.estimated_annual_reducevalue,
      pdd_status: item.pdd_status,
      is_active: item.is_active
    });
    setShowModal(true);
  };

  // ========== 儲存 (新增/更新) ==========
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingItem) {
        const updated = await projectsService.update(editingItem.id, formData);
        toast.success(t('message.saveSuccess'));
        await loadData();
        await reloadProjects();
        await logUpdate('projects', editingItem, updated);
      } else {
        const created = await projectsService.create(formData);
        toast.success(t('message.createSuccess'));
        await loadData();
        await reloadProjects();
        await logCreate('projects', created);
      }
      setShowModal(false);
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('message.saveFailed');
      toast.error(errorMsg);

      try {
        if (editingItem) {
          await logUpdate('projects', editingItem, formData as any, errorMsg);
        } else {
          await logCreate('projects', formData as any, errorMsg);
        }
      } catch (logErr) {
        console.error('Failed to log error:', logErr);
      }
    }
  };

  // ========== 刪除 ==========
  const handleDelete = async (item: Project) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('common.confirmDelete'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) {
      return;
    }

    try {
      await projectsService.deleteById(item.id);
      toast.success(t('message.deleteSuccess'));
      await loadData();
      await reloadProjects();
      await logDelete('projects', item);
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('message.deleteFailed');
      toast.error(errorMsg);
      await logDelete('projects', item, errorMsg);
    }
  };

  // ========== 活動參與者管理 ==========
  const handleAddParticipant = () => {
    setEditingParticipant(null);
    setParticipantFormData({
      ap_type: 1,
      ap_name: '',
      auth_party: parties[0]?.id || 0
    });
    setShowParticipantModal(true);
  };

  const handleEditParticipant = (participant: ActivityParticipant, index: number) => {
    setEditingParticipant({ participant, index });
    setParticipantFormData(participant);
    setShowParticipantModal(true);
  };

  const handleSaveParticipant = (e: React.FormEvent) => {
    e.preventDefault();

    const participants = [...(formData.activity_participants || [])];

    if (editingParticipant) {
      participants[editingParticipant.index] = participantFormData;
    } else {
      participants.push(participantFormData);
    }

    setFormData({ ...formData, activity_participants: participants });
    setShowParticipantModal(false);
  };

  const handleDeleteParticipant = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('common.confirmDelete'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) {
      return;
    }

    const participants = [...(formData.activity_participants || [])];
    participants.splice(index, 1);
    setFormData({ ...formData, activity_participants: participants });
  };

  // ========== 分頁計算 ==========
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('projects', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="projects" />
        <button
          className="btn-primary"
          onClick={handleAdd}
          disabled={!hasPermission('projects', 'create')}
        >
          + {t('common.add')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <>
          <div className="data-table-container">
            <table className="data-table">
              <thead className="table-header-dark-green">
                <tr>
                  <th style={{ width: '60px' }}>ID</th>
                  <th style={{ width: '25%' }}>{t('projects.projectTitle')}</th>
                  <th>{t('projects.sectoralScope')}</th>
                  <th>{t('projects.projectType')}</th>
                  <th>{t('projects.pddStatus')}</th>
                  <th>{t('common.isActive')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.project_title}</td>
                    <td>{renderSystemCodeNames(item.sectoral_scope_codes)}</td>
                    <td>{renderSystemCodeName(item.project_type_code)}</td>
                    <td>
                      <span className="status-badge">
                        {renderSystemCodeName(item.pdd_status_code)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${item.is_active ? 'active' : 'inactive'}`}>
                        {item.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(item)}
                        disabled={!hasPermission('projects', 'update')}
                        style={{ marginRight: '12px' }}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(item)}
                        disabled={!hasPermission('projects', 'delete')}
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分頁 */}
          <div className="pagination-container">
            <div className="pagination-info">
              <label>
                {t('common.itemsPerPage')}:
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <span className="pagination-text">
                {t('common.showing')} {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, data.length)} {t('common.of')} {data.length}
              </span>
            </div>
            <div className="pagination-buttons">
              <button
                className="btn-pagination"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx + 1}
                  className={`btn-pagination ${currentPage === idx + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(idx + 1)}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                className="btn-pagination"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          </div>
        </>
      )}

      {/* 新增/編輯對話框 */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h2>
                  📋 {t('projects.title')} - {editingItem ? t('common.editOperation') : t('common.createOperation')}
                </h2>
                <button type="button" className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-grid">
                  {/* 專案標題 */}
                  <div className="form-group full-width">
                    <label>{t('projects.projectTitle')} *</label>
                    <input
                      type="text"
                      value={formData.project_title}
                      onChange={(e) => setFormData({ ...formData, project_title: e.target.value })}
                      required
                    />
                  </div>

                  {/* UNFCCC專案識別編號 */}
                  <div className="form-group">
                    <label>{t('projects.pddNo')}</label>
                    <input
                      type="text"
                      value={formData.pdd_no}
                      onChange={(e) => setFormData({ ...formData, pdd_no: e.target.value })}
                    />
                  </div>

                  {/* 計畫版本號 */}
                  <div className="form-group">
                    <label>{t('projects.pddVersion')}</label>
                    <input
                      type="text"
                      value={formData.pdd_version}
                      onChange={(e) => setFormData({ ...formData, pdd_version: e.target.value })}
                    />
                  </div>

                  {/* 主辦國 */}
                  <div className="form-group">
                    <label>{t('projects.hostParty')} *</label>
                    <select
                      value={formData.host_party}
                      onChange={(e) => setFormData({ ...formData, host_party: Number(e.target.value) })}
                      required
                      style={{ maxWidth: '100%' }}
                    >
                      <option value="">請選擇...</option>
                      {parties.map((party) => {
                        const cname = htmlToText(party.code_cname);
                        const ename = party.code_ename ? htmlToText(party.code_ename) : '';
                        let displayText;
                        if (i18n.language === 'zh-TW') {
                          // 中文語系: (code) 中文 (English)
                          displayText = ename ? `(${party.code}) ${cname} (${ename})` : `(${party.code}) ${cname}`;
                        } else {
                          // 英文語系: (code) English
                          displayText = `(${party.code}) ${ename || cname}`;
                        }
                        return (
                          <option key={party.id} value={party.id} title={displayText}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* 參與國 */}
                  <div className="form-group">
                    <label>{t('projects.participatingParty')} *</label>
                    <select
                      value={formData.participating_party}
                      onChange={(e) => setFormData({ ...formData, participating_party: Number(e.target.value) })}
                      required
                      style={{ maxWidth: '100%' }}
                    >
                      <option value="">請選擇...</option>
                      {parties.map((party) => {
                        const cname = htmlToText(party.code_cname);
                        const ename = party.code_ename ? htmlToText(party.code_ename) : '';
                        let displayText;
                        if (i18n.language === 'zh-TW') {
                          // 中文語系: (code) 中文 (English)
                          displayText = ename ? `(${party.code}) ${cname} (${ename})` : `(${party.code}) ${cname}`;
                        } else {
                          // 英文語系: (code) English
                          displayText = `(${party.code}) ${ename || cname}`;
                        }
                        return (
                          <option key={party.id} value={party.id} title={displayText}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* 專案類別 */}
                  <div className="form-group">
                    <label>{t('projects.projectType')} *</label>
                    <select
                      value={formData.project_type}
                      onChange={(e) => setFormData({ ...formData, project_type: Number(e.target.value) })}
                      required
                    >
                      {projectTypes.map((type) => {
                        const isChinese = i18n.language === 'zh-TW';
                        const displayText = isChinese
                          ? `${type.code_cname}${type.code_ename ? ` (${type.code_ename})` : ''}`
                          : `${type.code_ename || type.code_cname}${type.code_cname && !isChinese ? ` (${type.code_cname})` : ''}`;
                        return (
                          <option key={type.id} value={type.id}>
                            ({type.code}) {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* 計畫完成日期 */}
                  <div className="form-group">
                    <label>{t('projects.pddCompletedate')}</label>
                    <DateInput
                      value={formData.pdd_completedate || ''}
                      onChange={(v) => setFormData({ ...formData, pdd_completedate: v || undefined })}
                      disabled={!hasPermission('projects', 'update')}
                    />
                    <small style={{ color: '#999', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                      {i18n.language === 'zh-TW' ? '格式: DD/MM/YYYY (例如: 03/02/2026)' : 'Format: DD/MM/YYYY (e.g., 03/02/2026)'}
                    </small>
                  </div>

                  {/* 適用方法論 */}
                  <div className="form-group full-width">
                    <label>{t('projects.methodologies')} *</label>
                    <select
                      multiple
                      value={formData.methodologies.map(String)}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions);
                        const selectedIds = selectedOptions.map(option => Number(option.value));
                        setFormData({ ...formData, methodologies: selectedIds });
                      }}
                      required
                      style={{ maxWidth: '100%', minHeight: '120px' }}
                    >
                      {methodologies.map((method) => {
                        const cname = htmlToText(method.code_cname);
                        const ename = method.code_ename ? htmlToText(method.code_ename) : '';
                        let displayText;
                        if (i18n.language === 'zh-TW') {
                          // 中文語系: (code) 中文 (English)
                          displayText = ename ? `(${method.code}) ${cname} (${ename})` : `(${method.code}) ${cname}`;
                        } else {
                          // 英文語系: (code) English
                          displayText = `(${method.code}) ${ename || cname}`;
                        }
                        return (
                          <option key={method.id} value={method.id} title={displayText}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                    <small style={{ color: '#666', fontSize: '0.9em' }}>
                      {i18n.language === 'zh-TW' ? '按住 Ctrl 或 Cmd 鍵可選擇多個項目' : 'Hold Ctrl or Cmd to select multiple items'}
                    </small>
                  </div>

                  {/* 部門範疇 (多選) */}
                  <div className="form-group full-width">
                    <label>{t('projects.sectoralScope')} *</label>
                    <select
                      multiple
                      value={formData.sectoral_scope.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, option => Number(option.value));
                        setFormData({ ...formData, sectoral_scope: selected });
                      }}
                      required
                      style={{ maxWidth: '100%', minHeight: '120px' }}
                    >
                      {sectoralScopes.map((scope) => {
                        const cname = htmlToText(scope.code_cname);
                        const ename = scope.code_ename ? htmlToText(scope.code_ename) : '';
                        let displayText;
                        if (i18n.language === 'zh-TW') {
                          displayText = ename ? `(${scope.code}) ${cname} (${ename})` : `(${scope.code}) ${cname}`;
                        } else {
                          displayText = `(${scope.code}) ${ename || cname}`;
                        }
                        return (
                          <option key={scope.id} value={scope.id} title={displayText}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                    <small style={{ color: '#888', marginTop: '4px' }}>
                      {i18n.language === 'zh-TW' ? '按住 Ctrl 或 Cmd 鍵可選擇多個項目' : 'Hold Ctrl or Cmd to select multiple items'}
                    </small>
                  </div>

                  {/* 標準基準線 */}
                  <div className="form-group full-width">
                    <label>{t('projects.standardBaselines')}</label>
                    {showModal && (
                      <RichTextEditor
                        key={`baselines-${editingItem?.id || 'new'}`}
                        value={editingItem?.standard_baselines || formData.standard_baselines || ''}
                        onChange={(html) => setFormData({ ...formData, standard_baselines: html })}
                        placeholder={i18n.language === 'zh-TW' ? '請輸入標準基準線說明...' : 'Please enter standard baselines description...'}
                      />
                    )}
                  </div>

                  {/* 預估減量值 */}
                  <div className="form-group">
                    <label>{t('projects.estimatedAnnualReducevalue')} *</label>
                    <div style={{ display: 'flex', gap: '0' }}>
                      <input
                        type="number"
                        value={formData.estimated_annual_reducevalue}
                        onChange={(e) => setFormData({ ...formData, estimated_annual_reducevalue: parseInt(e.target.value) || 0 })}
                        required
                        style={{
                          flex: 1,
                          borderRadius: '4px 0 0 4px',
                          borderRight: 'none'
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #ddd',
                        borderLeft: 'none',
                        borderRadius: '0 4px 4px 0',
                        color: '#666',
                        whiteSpace: 'nowrap',
                        fontSize: '14px'
                      }}>
                        tCO{renderHTML('&#8322;')}e/year
                      </div>
                    </div>
                  </div>

                  {/* 活動參與者 */}
                  <div className="form-group full-width">
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {t('projects.activityParticipants')}
                      <button type="button" className="btn-secondary" onClick={handleAddParticipant}>
                        + {t('common.add')}
                      </button>
                    </label>
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead className="table-header-dark-green">
                          <tr>
                            <th>{t('projects.apType')}</th>
                            <th>{t('projects.apName')}</th>
                            <th>{t('projects.authParty')}</th>
                            <th>{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.activity_participants || []).map((participant, index) => (
                            <tr key={index}>
                              <td>{participant.ap_type === 1 ? t('projects.apType1') : t('projects.apType2')}</td>
                              <td>{participant.ap_name}</td>
                              <td>{parties.find(p => p.id === participant.auth_party)?.code_cname || '-'}</td>
                              <td className="actions">
                                <button type="button" className="btn-edit" onClick={() => handleEditParticipant(participant, index)} style={{ marginRight: '12px' }}>
                                  {t('common.edit')}
                                </button>
                                <button type="button" className="btn-delete" onClick={() => handleDeleteParticipant(index)}>
                                  {t('common.delete')}
                                </button>
                              </td>
                            </tr>
                          ))}
                          {(!formData.activity_participants || formData.activity_participants.length === 0) && (
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                                {t('common.noData')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* 專案進度 */}
                  <div className="form-group">
                    <label>{t('projects.pddStatus')} *</label>
                    <select
                      value={formData.pdd_status}
                      onChange={(e) => setFormData({ ...formData, pdd_status: Number(e.target.value) })}
                      required
                      style={{ maxWidth: '100%' }}
                    >
                      {pddStatuses.map((status) => {
                        const isChinese = i18n.language === 'zh-TW';
                        const displayText = isChinese
                          ? `${status.code_cname}${status.code_ename ? ` (${status.code_ename})` : ''}`
                          : `${status.code_ename || status.code_cname}${status.code_cname && !isChinese ? ` (${status.code_cname})` : ''}`;
                        return (
                          <option key={status.id} value={status.id}>
                            ({status.code}) {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* 啟用狀態 */}
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        style={{ marginRight: '8px' }}
                      />
                      {t('common.isActive')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 活動參與者對話框 */}
      {showParticipantModal && (
        <div className="modal-overlay" onClick={() => setShowParticipantModal(false)}>
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSaveParticipant}>
              <div className="modal-header">
                <h2>{editingParticipant ? t('common.editOperation') : t('common.createOperation')}</h2>
                <button type="button" className="modal-close" onClick={() => setShowParticipantModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>{t('projects.apType')} *</label>
                    <select
                      value={participantFormData.ap_type}
                      onChange={(e) => setParticipantFormData({ ...participantFormData, ap_type: Number(e.target.value) })}
                      required
                    >
                      <option value={1}>{t('projects.apType1')}</option>
                      <option value={2}>{t('projects.apType2')}</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>{t('projects.apName')} *</label>
                    <input
                      type="text"
                      value={participantFormData.ap_name}
                      onChange={(e) => setParticipantFormData({ ...participantFormData, ap_name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>{t('projects.authParty')} *</label>
                    <select
                      value={participantFormData.auth_party}
                      onChange={(e) => setParticipantFormData({ ...participantFormData, auth_party: Number(e.target.value) })}
                      required
                      style={{ maxWidth: '100%' }}
                    >
                      <option value="">請選擇...</option>
                      {parties.map((party) => {
                        const cname = htmlToText(party.code_cname);
                        const ename = party.code_ename ? htmlToText(party.code_ename) : '';
                        let displayText;
                        if (i18n.language === 'zh-TW') {
                          // 中文語系: (code) 中文 (English)
                          displayText = ename ? `(${party.code}) ${cname} (${ename})` : `(${party.code}) ${cname}`;
                        } else {
                          // 英文語系: (code) English
                          displayText = `(${party.code}) ${ename || cname}`;
                        }
                        return (
                          <option key={party.id} value={party.id} title={displayText}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowParticipantModal(false)}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
